import { Component, computed, effect, inject, input, linkedSignal, OnDestroy, Signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideCheck, lucidePen, lucideSendHorizontal } from '@ng-icons/lucide';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmLabel } from '@spartan-ng/helm/label';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ChipComponent } from '@shared/components/chip/chip.component';
import { EstVerrouilleService } from '@shared/services/est-verrouille.service';
import { Référentiel } from '../../administration/gestion-référentiels/models/référentiel.model';
import { Cartographie } from '../../cartographie/models/cartographie.model';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../shared/components/expansion-panel/expansion-panel.component';
import { ToastrSuccessComponent } from '../../shared/components/toastr/toastr-success.component';
import { URL_FRANCE_COMPETENCES_RECHERCHE } from '../../shared/models/urls-france-compétences';
import { TrierParPipe } from '../../shared/pipes/trier-par.pipe';
import { BlocEtatComponent } from '../components/bloc-etat/bloc-etat.component';
import { CroisementÉtatComponent } from '../components/croisement-etat/croisement-état.component';
import { BlocCompétences } from '../models/bloc-compétences';
import { CroisementÉtat } from '../models/croisement-état';
import { GetIdFichePipe } from '../pipes/get-id-fiche.pipe';
import { CertificationStore } from '../store/certification.store';

@Component({
  selector: 'app-croisement-referentiel-form',
  imports: [
    CroisementÉtatComponent,
    ReactiveFormsModule,
    GetIdFichePipe,
    ExpansionPanel,
    RouterModule,
    HlmButton,
    NgIcon,
    BlocEtatComponent,
    HlmLabel,
    TrierParPipe,
    HlmTooltipImports,
    ChipComponent,
  ],
  templateUrl: './croisement-référentiel-form.component.html',
  providers: [provideIcons({ lucideArrowLeft, lucideSendHorizontal, lucideCheck, lucidePen })],
})
export class CroisementRéférentielFormComponent implements OnDestroy {
  readonly #store = inject(CertificationStore);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #canEditService = inject(CanEditService);
  readonly #estVerrouilleService = inject(EstVerrouilleService);

  readonly codeFiche = input.required<string>();
  readonly roles = input.required<string[]>();

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();

  readonly certification = computed(() => this.#store.entityMap()[this.codeFiche()]);

  protected readonly idsBlocs = computed(() => this.certification()?.blocsCompetences.map((x) => 'bloc-' + x.id));
  protected readonly blocsCompetences = linkedSignal(() => this.certification()?.blocsCompetences);

  protected peutModifier = computed(
    () =>
      this.certification().etatCroisement !== CroisementÉtat.TERMINE &&
      this.#canEditService.check(
        this.referentiel().equipeResponsable,
        this.roles(),
        LivrablePrincipal.PARCOURS_CROISEMENT,
        this.cartographie(),
        this.referentiel(),
      ),
  );

  readonly estVerrouille = computed(() =>
    this.#estVerrouilleService.check(LivrablePrincipal.PARCOURS_CROISEMENT, this.cartographie(), this.referentiel()),
  );

  urlFranceCompetencesRecherche = URL_FRANCE_COMPETENCES_RECHERCHE;

  readonly actionBlocCompetences: Signal<ExpansionPanelMenuAction<BlocCompétences>[]> = computed(() =>
    this.peutModifier()
      ? [
          {
            icon: 'lucidePen',
            label: 'Éditer le bloc de compétences',
            action: (bloc: BlocCompétences | undefined) => this.goToEditCroidement(bloc),
          },
        ]
      : [],
  );

  constructor() {
    effect(() => {
      this.#store.parCodeFiche({ codeFiche: this.codeFiche(), idReferentiel: this.referentiel().id });
    });
  }

  ngOnDestroy(): void {
    toast.dismiss();
  }

  goToEditCroidement(bloc: BlocCompétences | undefined) {
    if (!bloc) return;

    this.#router.navigate([bloc.codeBloc], { relativeTo: this.#route });
  }

  getAllAptitudesBloc(bloc: BlocCompétences) {
    return bloc.competences
      .flatMap((c) => c.aptitudes)
      .filter((apt1, i, list) => list.findIndex((apt2) => apt2.id === apt1.id) === i);
  }

  toggleValidation(etat: string) {
    const toastrId = uuidv7();

    this.#store.modifierCroisement({
      idCertification: this.certification().id,
      état: etat === CroisementÉtat.TERMINE ? CroisementÉtat.TERMINE : CroisementÉtat.EN_COURS,
      idRéférentiel: this.referentiel().id,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message:
              etat === CroisementÉtat.TERMINE
                ? 'Le croisement a bien été validé.'
                : 'Le croisement est modifiable à nouveau.',
            id: toastrId,
          },
          id: toastrId,
        });
      },
    });
  }
}
